import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { FieldGroup } from '@xsolla/ui-core';
import { ChevronUp } from '@xsolla/ui-icons';

import { SmsCustomizationItem, SmsTemplate } from '../../../api';
import { useAnimatedCollapse } from '../../../hooks';
import { usePaths } from '../../../routes/routesHelper';
import { smsMaxLength } from '../../../utils';
import { PAStyledLinkNoUnderline } from '../../ui';
import {
  SmsCustomizationTitle,
  SmsCustomizeContentBox,
  SmsTemplateContent,
  SmsTemplateTitle,
  SmsTemplateWrapper,
  StyledTextarea,
  TemplateIconWrapper,
  TextareaWrapper
} from './style';

type SmsTemplateWithError = SmsTemplate & {
  error?: string;
};

type Props = {
  templates: SmsTemplateWithError[];
  locale: string;
  openByDefault: boolean;
  projectId: string;
  onChange: ({ locale, templates }: SmsCustomizationItem) => void;
};

type TemplatesType = 'phone_auth_code' | 'mfa_code';

const templatesType: TemplatesType[] = ['phone_auth_code', 'mfa_code'];
const smsDescriptionLink = (
  mfaPath: (projectId: string) => string,
  projectId: string,
  template_type: TemplatesType
) => {
  const links = {
    mfa_code: mfaPath(projectId),
    phone_auth_code: null
  };

  return links[template_type];
};

export default ({ templates, locale, openByDefault, onChange, projectId }: Props): JSX.Element => {
  const { mfaPath } = usePaths();
  const intl = useIntl();
  const [templatesState, setTemplatesState] = useState(() => {
    if (templates.length === 0) {
      return templatesType.map((item) => ({ template_type: item, body: '', error: '' }));
    }
    return templates;
  });
  const { collapseHandle, contentHeight, ref } = useAnimatedCollapse({ isOpen: openByDefault });

  const handleChange = useCallback(
    (e, template_type) => {
      const newTemplates = templatesState.map((item) => {
        if (item.template_type === template_type) {
          let error = '';
          if (e.target.value.trim() === '') {
            error = intl.formatMessage({ id: 'app.validation.required' });
          } else {
            if (e.target.value.length > smsMaxLength[locale]) {
              error = intl.formatMessage(
                { id: 'sms-customization.settings.limit-symbol.error' },
                { number: smsMaxLength[locale] }
              );
            }

            if (e.target.value.indexOf('{{code}}') === -1) {
              error = intl.formatMessage({ id: 'sms-customization.settings.not-code.error' });
            }
          }

          return {
            ...item,
            error,
            body: e.target.value
          };
        }
        return item;
      });
      setTemplatesState(newTemplates);
      onChange({ locale, templates: newTemplates });
    },
    [templatesState, templates]
  );

  return (
    <SmsTemplateWrapper>
      <SmsCustomizationTitle onClick={collapseHandle}>
        <TemplateIconWrapper isOpenContent={!!contentHeight}>
          <ChevronUp size={20} />
        </TemplateIconWrapper>
        {intl.formatMessage({ id: `form.language.${locale}` })}
      </SmsCustomizationTitle>
      <SmsCustomizeContentBox contentHeight={contentHeight} ref={ref}>
        {templatesType.map((item) => {
          const templateBody = templatesState?.filter(
            ({ template_type }) => template_type === item
          )[0];
          return (
            <SmsTemplateContent key={`sms_template_contend_${item}`}>
              <SmsTemplateTitle>
                {intl.formatMessage({ id: `sms-customization.settings.${item}.title` })}
              </SmsTemplateTitle>
              <FieldGroup
                footer={intl.formatMessage({
                  id: `sms-customization.settings.${item}.post-description`
                })}
                label={intl.formatMessage(
                  { id: `sms-customization.settings.${item}.description` },
                  {
                    a: (chunk: string) => (
                      <PAStyledLinkNoUnderline to={smsDescriptionLink(mfaPath, projectId, item)}>
                        {chunk}
                      </PAStyledLinkNoUnderline>
                    )
                  }
                )}
                size="lg">
                <TextareaWrapper>
                  <StyledTextarea
                    placeholder={intl.formatMessage({
                      id: `sms-customization.settings.${item}.placeholder`
                    })}
                    value={templateBody.body}
                    onChange={(e) => handleChange(e, item)}
                    name={`sms_${locale}_${item}`}
                    key={`sms_${locale}_${item}`}
                    errorMessage={templateBody.error}
                    resize
                  />
                </TextareaWrapper>
              </FieldGroup>
            </SmsTemplateContent>
          );
        })}
      </SmsCustomizeContentBox>
    </SmsTemplateWrapper>
  );
};
